'use client';
import { useMiniCartInfo } from '@/context/mini-cart';
import { useCartTrigger } from '@/hooks/Header/useCartTrigger';
import useIsMobile from '@/hooks/use-is-mobile';
import clsx from 'clsx';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { GET_ITEM_COUNT_QUERY } from './cartTrigger.gql';

const CartTrigger = props => {
  const { miniCartOpen, setMiniCartOpen, setShowNoItemDrawer } = props;
  const { hideCartTrigger, itemCount } = useCartTrigger({
    queries: {
      getItemCountQuery: GET_ITEM_COUNT_QUERY,
    },
  });
  const pathname = usePathname();
  const hideMiniCartDrawer = pathname === '/cart';
  const buttonAriaLabel = `Toggle mini cart. You have ${itemCount} items in your cart.`;
  const isMobile = useIsMobile();
  const itemCountDisplay = itemCount > 99 ? '99+' : itemCount;
  const openMiniCartTimerRef = useRef(null);
  const { setShouldQueryMiniCart } = useMiniCartInfo();

  const maybeItemCounter = (
    <span
      className={clsx(
        'absolute -right-1.5 -top-0.5 h-[14px] w-[14px] rounded-[50%] bg-80583F text-[10px] leading-none text-white',
        'flex items-center justify-center text-#403732',
      )}
    >
      {itemCountDisplay}
    </span>
  );

  useEffect(() => {
    return () => {
      if (openMiniCartTimerRef.current) {
        clearTimeout(openMiniCartTimerRef.current);
      }
    };
  }, []);

  return (
    <>
      {hideCartTrigger ? null : (
        <>
          <div className='relative hidden flex-none lg_block'>
            <button
              aria-expanded={miniCartOpen}
              aria-label={buttonAriaLabel}
              className='group  relative flex items-center gap-x-1'
              onClick={() => {
                if (hideMiniCartDrawer) {
                  return;
                }

                setMiniCartOpen(true);
                setShouldQueryMiniCart(true);

                if (openMiniCartTimerRef.current) {
                  clearTimeout(openMiniCartTimerRef.current);
                  openMiniCartTimerRef.current = null;
                }
              }}
              type='button'
              onMouseEnter={() => {
                if (hideMiniCartDrawer || isMobile) {
                  return;
                }

                openMiniCartTimerRef.current = setTimeout(() => {
                  setShouldQueryMiniCart(true);
                  setMiniCartOpen(true);
                }, 800);
              }}
              onMouseLeave={() => {
                if (isMobile) return;

                if (openMiniCartTimerRef.current) {
                  clearTimeout(openMiniCartTimerRef.current);
                  openMiniCartTimerRef.current = null;
                }
              }}
            >
              {cartIcon}
              {maybeItemCounter}
            </button>
          </div>

          {itemCount ? (
            <Link
              aria-expanded={miniCartOpen}
              aria-label='go to cart page'
              className='group relative block lg_hidden'
              href='/cart'
            >
              {cartIcon}
              {maybeItemCounter}
            </Link>
          ) : (
            <button
              type='button'
              className='relative block lg_hidden'
              onClick={() => {
                setShowNoItemDrawer(true);
              }}
              aria-label='toggle no item drawer'
            >
              {cartIcon}
              {maybeItemCounter}
            </button>
          )}
        </>
      )}
    </>
  );
};

const cartIcon = (
  <svg
    xmlns='http://www.w3.org/2000/svg'
    width='19'
    height='21'
    fill='none'
    viewBox='0 0 19 21'
  >
    <g
      className='stroke-#403732 lg_group-hover_stroke-80583F'
      strokeLinecap='round'
      strokeMiterlimit='10'
      strokeWidth='1.3'
      clipPath='url(#clip0_12071_8669)'
    >
      <path d='M5.59 8.926c0-4.33 1.36-7.87 3.95-7.84 2.55 0 3.95 3.51 3.95 7.84' />
      <path d='M2.97 19.734H16.1c.82 0 1.5-.61 1.54-1.38l.68-12.19H.75l.68 12.19c.04.77.72 1.38 1.54 1.38z' />
    </g>
    <defs>
      <clipPath id='clip0_12071_8669'>
        <path
          fill='#fff'
          d='M0 0H18.94V19.95H0z'
          transform='translate(.059 .438)'
        />
      </clipPath>
    </defs>
  </svg>
);

export default CartTrigger;
